import React, {useEffect} from 'react';
import {IForm} from "../../../../../libs/uiKit/fieldsBuilder/types";
import {useFormContext} from "react-hook-form";
import {FieldsBuilder} from "../../../../../libs/uiKit/fieldsBuilder";
import dayjs from "dayjs";
import {useCreateAgreementContext} from "../../ui/context";
import {IDaDataValue} from "../../../../../libs/uiKit/daData/types";
import {getFlatPrecalcForm} from "../../../flat";
import {Heading3} from "../../../../../libs/uiKit/globalStyledComponents";
import Hint from "../../../../../libs/uiKit/hint";
import {StyledHeaderWithQuestion, StyledRecalcPanel, StyledRecalcPanelText} from "./styles";
import ContragentIgniter from "../../widgets/contragent";
import {formatPersonDataToDataForm} from "../../ds/repositories/commands";
import {FieldColumnWrapper} from "../../../../../libs/uiKit/fieldsBuilder/styles";
import {StyledBigPremiumHeader, StyledCrossedPremiumHeader, StyledSmallApplyButton} from "../../../flat/styles";
import Button from "../../../../../libs/uiKit/button";
import {CloseIcon} from "../../../../../libs/uiKit/icons/Close";
import {useGlobalContext} from "../../../../../apps/main/ui/GlobalContext";
import {stringToMoney} from "../../../../../libs/uiKit/utils";

export type FlatStepForm = {
    flatPledgeAddressMatch?: boolean;
    flatPledgeAddress?: IDaDataValue | null;
    flatDateBeg?: Date | null;
    flatArea?: number;
    flatRoomsCount?: number;
    register?: boolean;
}

// принимает на вход isDisabled
const FlatStep = ({isFieldsDisabled, isContract}: { isFieldsDisabled?: boolean; isContract?: boolean }) => {
    const formHook = useFormContext();

    const {
        user: { isStaffMember },
    } = useGlobalContext();

    const {
        calcForm,
        isLoading,
        switchHiddenButtonsHandler,
        saveTimestamp
    } = useCreateAgreementContext();

    const phAddress = calcForm?.policyholder?.registrationAddress?.value;

    const premiumSum = calcForm?.flat?.premium_sum || 0;
    const oldPremiumSum = calcForm?.flat?.old_premium_sum || undefined;

    const [preserveDeleteIcon, setPreserveDeleteIcon] = React.useState<boolean>(false);
    const [stateIsChanged, setStateIsChanged] = React.useState<boolean>(false);

    useEffect(() => {
        setStateIsChanged(false);
        if(switchHiddenButtonsHandler){
            switchHiddenButtonsHandler(false);
        }
    }, [saveTimestamp]);

    const appliedPromocode = calcForm?.flat?.promo_code || '';
    const appliedDiscount = calcForm?.flat?.seller_discount_due_kv || 0;

    const [pledgeAddressMatch, dateBeg, promocodeValue, discountValue, policyHolderAddress]
        = formHook.watch(['flatPledgeAddressMatch', 'flatDateBeg', 'flatPromoCode', 'flatSellerDiscountDueKv', 'contragents[0].addr1']);

    const dateEnd: string = dateBeg ? dayjs(dateBeg).add(1, 'year').subtract(1, 'day').format('DD.MM.YYYY') : '';

    React.useEffect(() => {
        if (pledgeAddressMatch) {
            formHook.setValue('flatPledgeAddress', policyHolderAddress);
            formHook.setValue('flatPledgeAddressDisabled', policyHolderAddress?.value || '');
        }
    }, [pledgeAddressMatch, policyHolderAddress, formHook]);


    const form: IForm = {
        formHook,
        hotReload: true,
        fields: [
            {
                field: {
                    fieldType: 'checkbox',
                    fieldName: 'flatPledgeAddressMatch',
                    isRequired: false,
                    grid: 4,
                    label: "По адресу регистрации",
                    isDisabled: isFieldsDisabled,
                },
            },
            {
                isHidden: pledgeAddressMatch,
                field: {
                    fieldType: 'daData',
                    fieldName: `flatPledgeAddress`,
                    daDataType: 'address',
                    placeholder: 'Адрес объекта страхования',
                    isRequired: true,
                    isDisabled: isFieldsDisabled,
                },
            },
            {
                isHidden: !pledgeAddressMatch,
                field: {
                    fieldType: 'input',
                    fieldName: `flatPledgeAddressDisabled`,
                    placeholder: 'Адрес объекта страхования',
                    isDisabled: true,
                    defaultValue: phAddress,
                },
            },
            {
                field: {
                    fieldType: 'number',
                    fieldName: `flatArea`,
                    digitsAfterDot: 2,
                    grid: 6,
                    placeholder: 'Общая площадь, кв. м',
                    validationType: 'minMax',
                    maxValue: 1000,
                    errorMessage: 'Введите площадь',
                    isDisabled: isFieldsDisabled,
                },
            },
            {
                field: {
                    fieldType: 'number',
                    fieldName: `flatRoomsCount`,
                    placeholder: 'Количество комнат',
                    grid: 6,
                    validationType: 'minMax',
                    minValue: 1,
                    isDisabled: isFieldsDisabled,
                },
            },
        ],
    }

    const form2 = getFlatPrecalcForm(formHook, isFieldsDisabled, () => {
        setStateIsChanged(true);
        if(switchHiddenButtonsHandler){
            switchHiddenButtonsHandler(true);
        }
    });

    const formPromocodeDiscount: IForm = {
        formHook,
        hotReload: true,
        fields: [
            {
                isHidden: isStaffMember,
                field: {
                    fieldType: 'number',
                    fieldName: 'flatSellerDiscountDueKv',
                    placeholder: 'Скидка за счёт КВ, %',
                    grid: 6,
                    isDisabled: isFieldsDisabled,
                    iconAfter: discountValue || preserveDeleteIcon ? (discountValue !== appliedDiscount && !preserveDeleteIcon
                        ? <StyledSmallApplyButton>
                            <Button appearance='transparent' type='submit'>Применить</Button>
                        </StyledSmallApplyButton>
                        : <Button appearance='icon' type='submit' onClick={(): void => {
                            setPreserveDeleteIcon(true);
                            formHook.setValue('flatSellerDiscountDueKv', 0);
                            setTimeout(() => {
                                setPreserveDeleteIcon(false);
                            }, 1);
                        }
                        } icon={<CloseIcon />} />) : undefined
                },
            },
            {
                field: {
                    fieldType: 'input',
                    fieldName: 'flatPromoCode',
                    placeholder: 'Промокод',
                    grid: 6,
                    isDisabled: isFieldsDisabled,
                    iconAfter: promocodeValue || preserveDeleteIcon ? (promocodeValue !== appliedPromocode && !preserveDeleteIcon
                        ? <StyledSmallApplyButton>
                            <Button appearance='transparent' type='submit'>Применить</Button>
                        </StyledSmallApplyButton>
                        : <Button appearance='icon' type='submit' onClick={(): void => {
                            setPreserveDeleteIcon(true);
                            formHook.setValue('flatPromoCode', '');
                            setTimeout(() => {
                                setPreserveDeleteIcon(false);
                            }, 1);
                        }
                        } icon={<CloseIcon />} />) : undefined
                }
            }
        ]
    }

    const form3: IForm = {
        formHook,
        hotReload: true,
        fields: [
            {
                field: {
                    fieldType: 'date',
                    fieldName: 'flatDateBeg',
                    isRequired: true,
                    errorMessage: 'Выберите дату',
                    placeholder: 'Дата начала',
                    validationType: 'minMaxDate',
                    minDate: dayjs().add(5, 'days').toDate(),
                    maxDate: dayjs().add(31, 'years').toDate(),
                    grid: 3,
                    isDisabled: isFieldsDisabled,
                    calendarDirection: "up"
                },
            },
            {
                field: {
                    fieldType: 'input',
                    fieldName: 'flatDateEnd',
                    placeholder: 'Дата окончания',
                    isDisabled: true,
                    defaultValue: dateEnd,
                    grid: 3,
                },
            },
        ],
    }


  return <>
      {!isContract && <ContragentIgniter
          isShowShare={false}
          isShowSearchByIsn={true}
          number={0}
          arrayName='contragents'
          searchSuccessCallback={(contragent, number) => {
              const contForm = formatPersonDataToDataForm(contragent)
              Object.entries(contForm).forEach(([key, value]) => {
                    formHook.setValue(`contragents[${number}].${key}`, value);
              });
          }}
          contragentDisabled={isFieldsDisabled || false}
          postfix='страхователя'
          //defaultValues={policyholder}
          flatMode={true}
      />}
      <Heading3>Квартира</Heading3>
      <br/><br/>
        <FieldsBuilder {...form} />
        <FieldsBuilder {...form2} />
      {!isContract && <>
          <FieldsBuilder {...formPromocodeDiscount} />
          <StyledRecalcPanel>
              <StyledRecalcPanelText>Страховая премия</StyledRecalcPanelText>
              {!stateIsChanged &&
                  <StyledBigPremiumHeader>
                      {oldPremiumSum && <>
                          <StyledCrossedPremiumHeader>
                              {stringToMoney(oldPremiumSum)} RUB
                          </StyledCrossedPremiumHeader>
                          &nbsp;&nbsp;
                      </>}

                      {stringToMoney(premiumSum)} RUB
                  </StyledBigPremiumHeader>}
              {stateIsChanged &&
                  <Button appearance='filled' type='submit' isLoading={isLoading} style={{width: '25rem'}}>
                        Пересчитать
                  </Button>}

          </StyledRecalcPanel>
          <StyledHeaderWithQuestion style={{marginBottom: '2rem'}}>
              <Heading3>Период действия договора</Heading3>
              <Hint text="Дата начала договора не ранее 5 календарных дней от текущей даты"/>
          </StyledHeaderWithQuestion>
          <FieldsBuilder {...form3} />
      </>
      }
  </>
}

export default FlatStep;